<template>
    <div id="app">
        <drop-animation :class="{ 'animation-start': showDropAnimation }" />
        <transition name="fade" mode="out-in">
            <drop-overlay v-show="dragVisible" :class="{ 'overlay-active': dragVisible, 'fixed': isBlankLayout }" />
        </transition>
        <div class="area" :class="layout">
            <organization v-if="organization" :organization="organization" />
            <router-view />
            <upload-info />
            <powered-by v-if="isBlankLayout" />
        </div>
        <powered-by  v-if="!isBlankLayout" />
    </div>
</template>


<style lang="scss">
@import 'css/config';
@import 'css/transition';

html,
body {
    width: 100%;
    height: 100%;
    color: $body-color;
    background-color: $body-background !important;
    font-family: $font-family;
    position: relative;
    font-size: $font-size;
}

a {
    color: $link-color;
}

#app {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
}

.tooltip {
    margin-left: -5px;

    > div {
        white-space: normal;
        width: 300px;
        text-align: center;
    }
}

.fade-leave-to .tooltip {
    display: none;
}

.area {
    box-sizing: border-box;
    width: 100%;

    &.-box {
        width: 325px;
        background: #fff;
        border-radius: 4px;
        border: 1px solid $border-color;
    }

    &.-blank {
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: flex-end;
        padding-bottom: 20px;

        .message,
        .list,
        .organization,
        .signer,
        .uploadinfo {
            display: none;
        }

        #verification {
            .panel {
                border: 0;
                padding: 0;
                margin: 0;
                min-height: 0;

                .icon {
                    margin-bottom: 0 !important;
                }

                .browse {
                    > .drop-here,
                    > .or-browse {
                        display: none;
                    }
                }
            }
        }

        .powered-by {
            padding: 0;
            position: fixed;
            top: 100%;
            left: 50%;
            transform: translate(30px, -104px);

            img {
                margin-right: 0;
            }
        }

        .icon {
            cursor: pointer;
        }
    }
}
</style>

<script>
import DropOverlay from "./components/DropOverlay.vue";
import DropAnimation from "./components/DropAnimation.vue";
import UploadInfo from "./components/UploadInfo";
import PoweredBy from "./components/PoweredBy";
import Organization from "./components/Organization";

export default {
    name: "app",
    components: {
        DropOverlay,
        DropAnimation,
        PoweredBy,
        Organization,
        UploadInfo
    },
    props: {
        file: {}
    },
    data: () => ({
        dragVisible: false,
        showDropAnimation: false
    }),
    computed: {
        verificationState() {
            return this.$store.getters.verificationState;
        },
        organization() {
            return this.$store.getters.organization;
        },
        signer() {
            return this.$store.getters.signer;
        },
        layout() {
            return this.$route.query.layout ? `-${this.$route.query.layout}` : false;
        },
        isBlankLayout() {
            return this.$route.query.layout === 'blank';
        }
    },
    methods: {
        onDragEnter(e) {
            this.lastTarget = e.target;
            this.dragVisible = true;
        },
        onDragLeave(e) {
            if (e.target === this.lastTarget) {
                this.dragVisible = false;
            }
        },
        onDragOver(e) {
            e.preventDefault();
        },
        onDrop(ev) {
            ev.preventDefault();

            this.dragVisible = false;

            if (ev.dataTransfer.items) {
                // Use DataTransferItemList interface to access the file(s)
                for (let i = 0; i < ev.dataTransfer.items.length; i++) {
                    // If dropped items aren't files, reject them
                    if (ev.dataTransfer.items[i].kind === "file") {
                        this.processFile(ev.dataTransfer.items[i].getAsFile());
                    }
                }
            }

            this.removeDragData(ev);
        },
        removeDragData(ev) {
            if (ev.dataTransfer.items) {
                // Use DataTransferItemList interface to remove the drag data
                ev.dataTransfer.items.clear();
            } else {
                // Use DataTransfer interface to remove the drag data
                ev.dataTransfer.clearData();
            }
        },
        processFile(file) {
            this.$store.dispatch("reset");
            this.$store.dispatch("verify", file);
        },
        processHash(hash) {
            this.$store.dispatch("reset");
            this.$store.dispatch("verify", hash);
        },
        onResetApp() {
            this.$store.dispatch("reset");
        }
    },
    async mounted() {
        const { organization, signer } = this.$route.params;

        window.addEventListener("dragenter", this.onDragEnter);
        window.addEventListener("dragleave", this.onDragLeave);
        window.addEventListener("dragover", this.onDragOver);
        window.addEventListener("drop", this.onDrop);

        if (organization) {
            await this.$store.dispatch("organization", organization);

            if (!this.organization) {
                this.$router.push("/" + this.$route.params.lang + "/error/org");
            }
        } else if (signer) {
            await this.$store.dispatch("signer", signer);

            if (!this.signer) {
                this.$router.push("/" + this.$route.params.lang + "/error/signer");
            }
        }
    },
    created() {
        if (this.file) {
            this.processFile("file://" + this.file);
        }

        if (this.$route.query.hash) {
            this.processHash(this.$route.query.hash);
        }
    },
    beforeDestroy() {
        window.removeEventListener("dragenter", this.onDragEnter);
        window.removeEventListener("dragleave", this.onDragLeave);
        window.removeEventListener("dragover", this.onDragOver);
        window.removeEventListener("drop", this.onDrop);
    }
};
</script>
